import React, { useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAIFormAssistant } from "@/hooks/use-ai-form-assistant";
import { AIAssistantCard } from "@/components/ai-assistant/ai-assistant-card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormFieldType } from "@api/shared-runtime/enums";
import { FormFieldEditor } from "./form-field-editor";

const formFieldSchema = z.object({
  label: z.string().min(1, "Label is required").max(500),
  description: z.string().max(2000).nullable().optional(),
  fieldType: z.nativeEnum(FormFieldType),
  sortOrder: z.number().int().min(0),
  isRequired: z.boolean(),
  options: z.array(z.string()).nullable().optional(),
});

export const orgFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  description: z.string().max(2000).nullable().optional(),
  fields: z.array(formFieldSchema).min(1, "At least one field is required"),
});

export type OrgFormFormValues = z.infer<typeof orgFormSchema>;

interface OrgFormFormProps {
  defaultValues?: Partial<OrgFormFormValues>;
  onSubmit: (values: OrgFormFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function OrgFormForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: OrgFormFormProps) {
  const form = useForm<OrgFormFormValues>({
    resolver: zodResolver(orgFormSchema),
    defaultValues: {
      name: "",
      description: "",
      fields: [
        {
          label: "",
          description: "",
          fieldType: FormFieldType.Text,
          sortOrder: 0,
          isRequired: true,
          options: [],
        },
      ],
      ...defaultValues,
    },
  });

  const aiAssistant = useAIFormAssistant({
    formType: "orgForm",
    form,
  });

  const aiStarterPrompts = [
    "New hire onboarding form: emergency contact, scrub size, preferred name, and start-date availability",
    "Policy acknowledgment form: HIPAA, code of conduct, and confidentiality agreement",
  ];

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "fields",
  });

  // Track which field indices should start expanded (newly added fields).
  // We store the index of the field that was just appended so FormFieldEditor
  // can default to open. The Set is consumed during render and cleared after.
  const newFieldIndicesRef = useRef<Set<number>>(new Set());

  const handleAddField = () => {
    const nextIndex = fields.length;
    newFieldIndicesRef.current.add(nextIndex);
    append({
      label: "",
      description: "",
      fieldType: FormFieldType.Text,
      sortOrder: fields.length,
      isRequired: true,
      options: [],
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) {
      return;
    }
    move(index, index - 1);
    const items = form.getValues("fields");
    items.forEach((_, i) => {
      form.setValue(`fields.${i}.sortOrder`, i);
    });
  };

  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) {
      return;
    }
    move(index, index + 1);
    const items = form.getValues("fields");
    items.forEach((_, i) => {
      form.setValue(`fields.${i}.sortOrder`, i);
    });
  };

  const handleSubmit = (values: OrgFormFormValues) => {
    const fieldsWithOrder = values.fields.map((field, i) => ({
      ...field,
      sortOrder: i,
      // Normalize: only keep options for select types, null otherwise
      options:
        field.fieldType === FormFieldType.Select ||
        field.fieldType === FormFieldType.MultiSelect
          ? (field.options ?? []).filter((o) => o.trim().length > 0)
          : null,
    }));
    onSubmit({ ...values, fields: fieldsWithOrder });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-6 px-4 sm:px-6"
      >
        {/* AI Assistant — front and center */}
        <AIAssistantCard
          messages={aiAssistant.messages}
          onSendMessage={aiAssistant.sendMessage}
          isLoading={aiAssistant.isLoading}
          onClearConversation={aiAssistant.clearConversation}
          title="Build your form with AI"
          emptyStatePrompt="Describe the form you want and I'll create the fields."
          inputPlaceholder="e.g. Describe the form you want..."
          starterPrompts={aiStarterPrompts}
          pendingAttachments={aiAssistant.pendingAttachments}
          onAddAttachment={aiAssistant.addAttachment}
          onRemoveAttachment={aiAssistant.removeAttachment}
          hasInvalidAttachment={aiAssistant.hasInvalidAttachment}
        />

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Form Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Provider Intake Form" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe what this form is for..."
                    rows={2}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-base font-semibold">Form Fields</Label>
            <span className="text-sm text-gray-500">
              {fields.length} field{fields.length !== 1 ? "s" : ""}
            </span>
          </div>

          {form.formState.errors.fields?.root && (
            <p className="text-sm text-red-600 mb-3">
              {form.formState.errors.fields.root.message}
            </p>
          )}

          <div className="space-y-3">
            {fields.map((field, index) => {
              const isNew = newFieldIndicesRef.current.has(index);
              if (isNew) {
                newFieldIndicesRef.current.delete(index);
              }
              return (
                <FormFieldEditor
                  key={field.id}
                  control={form.control}
                  fieldIndex={index}
                  onMoveUp={() => handleMoveUp(index)}
                  onMoveDown={() => handleMoveDown(index)}
                  onRemove={() => remove(index)}
                  isFirst={index === 0}
                  isLast={index === fields.length - 1}
                  isNew={isNew}
                />
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-4 w-full border-dashed border-blue-300 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            onClick={handleAddField}
          >
            <Plus size={14} className="mr-1.5" />
            Add Field
          </Button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 pb-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Form"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
